import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import { Button, Input, Modal, Checkbox, H5 } from '@pay-com/components'
import { Website } from 'pages/Onboarding'
import { getErrorMessage } from 'utils/get-error-message'

const stripDomainPrefix = (value: string) =>
  value
    .trim()
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')

const createWebsiteSchema = (existingWebsites: Website[]) =>
  z
    .object({
      domain: z
        .string()
        .trim()
        .transform(stripDomainPrefix)
        .pipe(
          z
            .string()
            .regex(
              /^([a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/,
              { message: 'invalidDomain' }
            )
        )
        .transform(v => v.toLowerCase())
        .refine(
          v => !existingWebsites.some(w => w.domain.toLowerCase() === v),
          { message: 'duplicateDomain' }
        ),
      platformRequiresTestUser: z.boolean(),
      login: z.string().optional(),
      password: z.string().optional()
    })
    .superRefine((data, ctx) => {
      if (!data.platformRequiresTestUser) return

      if (!data.login?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'usernameRequired',
          path: ['login']
        })
      }
      if (!data.password?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'passwordRequired',
          path: ['password']
        })
      }
    })

type WebsiteFormValues = z.input<ReturnType<typeof createWebsiteSchema>>

type WebsiteManagementModalProps = {
  isOpen: boolean
  defaultValues?: Website
  existingWebsites?: Website[]
  onConfirm: (website: Website) => void
  onDismiss: () => void
  isEdit?: boolean
}

const emptyDefaults: WebsiteFormValues = {
  domain: '',
  platformRequiresTestUser: false,
  login: '',
  password: ''
}

export const WebsiteManagementModal = ({
  isOpen,
  defaultValues,
  existingWebsites = [],
  onConfirm,
  onDismiss,
  isEdit = false
}: WebsiteManagementModalProps) => {
  const { t } = useTranslation()
  const { t: tErrors } = useTranslation('errors')
  const errorMsg = (msg?: string) => getErrorMessage(tErrors, msg)

  const methods = useForm<WebsiteFormValues>({
    resolver: zodResolver(createWebsiteSchema(existingWebsites)),
    defaultValues: defaultValues
      ? {
          domain: defaultValues.domain,
          platformRequiresTestUser: !!defaultValues.platformRequiresTestUser,
          login: defaultValues.login ?? '',
          password: defaultValues.password ?? ''
        }
      : emptyDefaults,
    mode: 'onBlur',
    reValidateMode: 'onChange'
  })

  useEffect(() => {
    if (isOpen) {
      methods.reset(
        defaultValues
          ? {
              domain: defaultValues.domain,
              platformRequiresTestUser:
                !!defaultValues.platformRequiresTestUser,
              login: defaultValues.login ?? '',
              password: defaultValues.password ?? ''
            }
          : emptyDefaults
      )
    }
  }, [isOpen, defaultValues])

  const platformRequiresTestUser = methods.watch('platformRequiresTestUser')

  const handleSubmit = methods.handleSubmit(data => {
    onConfirm({
      domain: stripDomainPrefix(data.domain).toLowerCase(),
      platformRequiresTestUser: data.platformRequiresTestUser,
      login: data.platformRequiresTestUser
        ? data.login?.trim() || undefined
        : undefined,
      password: data.platformRequiresTestUser
        ? data.password || undefined
        : undefined
    })
  })

  const domainError = methods.formState.errors.domain
  const loginError = methods.formState.errors.login
  const passwordError = methods.formState.errors.password

  return (
    <Modal.Root open={isOpen} onOpenChange={open => !open && onDismiss()}>
      <Modal>
        <Modal.Header>
          <H5 className="!text-[26px]">
            {isEdit
              ? t('generalInformation.fields.websites.editTitle')
              : t('generalInformation.fields.websites.addTitle')}
          </H5>
        </Modal.Header>
        <Modal.Subtitle className="!mt-0 mb-4">
          {t('generalInformation.fields.websites.tooltip')}
        </Modal.Subtitle>
        <Modal.Content>
          <div className="flex flex-col gap-4">
            <Input
              label={t('generalInformation.fields.websites.domainLabel')}
              placeholder={t(
                'generalInformation.fields.websites.domainPlaceholder'
              )}
              disabled={isEdit}
              {...methods.register('domain')}
              error={!!domainError}
              errorMessage={errorMsg(domainError?.message)}
            />
            <Checkbox
              checked={platformRequiresTestUser}
              onChange={() =>
                methods.setValue(
                  'platformRequiresTestUser',
                  !platformRequiresTestUser
                )
              }
              label={t(
                'generalInformation.fields.websites.platformRequiresTestUser'
              )}
            />
            {platformRequiresTestUser && (
              <Input
                label={t('generalInformation.fields.websites.usernameLabel')}
                placeholder={t(
                  'generalInformation.fields.websites.usernamePlaceholder'
                )}
                {...methods.register('login')}
                error={!!loginError}
                errorMessage={errorMsg(loginError?.message)}
              />
            )}
            {platformRequiresTestUser && (
              <Input
                label={t('generalInformation.fields.websites.passwordLabel')}
                placeholder={t(
                  'generalInformation.fields.websites.passwordPlaceholder'
                )}
                type="password"
                {...methods.register('password')}
                error={!!passwordError}
                errorMessage={errorMsg(passwordError?.message)}
              />
            )}
          </div>
        </Modal.Content>
        <Modal.Actions className="mt-4">
          <Button variant="text" type="button" onClick={onDismiss}>
            {t('actions.cancel')}
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={!methods.watch('domain').length}
          >
            {isEdit
              ? t('actions.save')
              : t('generalInformation.fields.websites.addButton')}
          </Button>
        </Modal.Actions>
      </Modal>
    </Modal.Root>
  )
}
